/**
 * Epic 278 Phase 3 Slice C — URL-persisted saved views over Attention / registers.
 * Preset filters only; no server store; no bulk mutations.
 */

import type { AttentionMetricKey } from './workspaceAttention';
import type {
  CaseRegisterRow,
  RecordRegisterRow,
  ScanRegisterRow,
} from './workspaceRegisters';
import { isWorkspaceDueDateOverdue } from './workspaceDueDates';
import { filterToLatestRelevantScans } from './workspaceScansOrganize';
import { buyerScanDefinitionDisplayName } from '../buyerFacing/conditionDisplayNames';

export const SAVED_VIEW_IDS = [
  'my_open_work',
  'awaiting_signoff',
  'failed_blocked',
  'coverage_issues',
  'scheduled_upcoming',
  'latest_per_case',
  'manual_only',
  'new_activity',
  'overdue_work',
  'unowned_work',
] as const;

export type SavedViewId = (typeof SAVED_VIEW_IDS)[number];

export type SavedViewTab = 'attention' | 'cases' | 'scans' | 'records';

export type WorkspaceSavedViewDef = {
  id: SavedViewId;
  label: string;
  /** Primary tab when the view is selected. */
  tab: SavedViewTab;
  /** Attention metric to apply (null clears the attention filter). */
  attentionFilter: AttentionMetricKey | null;
  supportedTabs: SavedViewTab[];
};

const SAVED_VIEW_PARAM = 'view';
const SCAN_DEFINITION_SCOPE_PARAM = 'scan_definition';

const PRESETS: Record<SavedViewId, WorkspaceSavedViewDef> = {
  my_open_work: {
    id: 'my_open_work',
    label: 'My open work',
    tab: 'attention',
    attentionFilter: null,
    supportedTabs: ['attention', 'cases'],
  },
  awaiting_signoff: {
    id: 'awaiting_signoff',
    label: 'Awaiting sign-off',
    tab: 'attention',
    attentionFilter: 'awaiting_signoff',
    supportedTabs: ['attention', 'cases', 'scans'],
  },
  failed_blocked: {
    id: 'failed_blocked',
    label: 'Failed/blocked',
    tab: 'scans',
    attentionFilter: null,
    supportedTabs: ['scans'],
  },
  coverage_issues: {
    id: 'coverage_issues',
    label: 'Coverage issues',
    tab: 'attention',
    attentionFilter: 'coverage_run',
    // Cases: brief view set excludes Coverage issues (Attention + Scans only).
    supportedTabs: ['attention', 'scans'],
  },
  scheduled_upcoming: {
    id: 'scheduled_upcoming',
    label: 'Scheduled upcoming',
    tab: 'scans',
    attentionFilter: null,
    supportedTabs: ['scans'],
  },
  latest_per_case: {
    id: 'latest_per_case',
    label: 'Latest per case',
    tab: 'scans',
    attentionFilter: null,
    supportedTabs: ['scans'],
  },
  manual_only: {
    id: 'manual_only',
    label: 'Manual only',
    tab: 'scans',
    attentionFilter: null,
    supportedTabs: ['scans'],
  },
  new_activity: {
    id: 'new_activity',
    label: 'New activity',
    tab: 'attention',
    attentionFilter: 'new_activity',
    supportedTabs: ['attention', 'cases', 'records'],
  },
  overdue_work: {
    id: 'overdue_work',
    label: 'Overdue work',
    tab: 'attention',
    attentionFilter: 'overdue',
    supportedTabs: ['attention', 'cases'],
  },
  unowned_work: {
    id: 'unowned_work',
    label: 'Unowned work',
    tab: 'attention',
    attentionFilter: 'unowned',
    supportedTabs: ['attention', 'cases'],
  },
};

export function savedViewParam(): string {
  return SAVED_VIEW_PARAM;
}

/** URL param for Scans register definition scope (schedule card → table). */
export function scanDefinitionScopeParam(): string {
  return SCAN_DEFINITION_SCOPE_PARAM;
}

export function listSavedViews(): WorkspaceSavedViewDef[] {
  return SAVED_VIEW_IDS.map((id) => PRESETS[id]);
}

/** Views shown as chips on a tab — unsupported views are omitted (not greyed). */
export function listSavedViewsForTab(tab: SavedViewTab): WorkspaceSavedViewDef[] {
  return listSavedViews().filter((view) => view.supportedTabs.includes(tab));
}

export function getSavedView(id: SavedViewId): WorkspaceSavedViewDef {
  return PRESETS[id];
}

export function isSavedViewSupported(viewId: SavedViewId, tab: SavedViewTab): boolean {
  return PRESETS[viewId].supportedTabs.includes(tab);
}

export function savedViewLabel(viewId: SavedViewId | null | undefined): string | null {
  if (!viewId) return null;
  return PRESETS[viewId]?.label ?? null;
}

export function resolveScanDefinitionScope(raw: string | null | undefined): string | null {
  const value = String(raw || '').trim();
  return value || null;
}

/** Select or clear the Scans definition scope (stores definition id). */
export function applyScanDefinitionScopeToSearchParams(
  params: URLSearchParams,
  definitionId: string | null,
): void {
  if (!definitionId) {
    params.delete(SCAN_DEFINITION_SCOPE_PARAM);
    return;
  }
  params.set(SCAN_DEFINITION_SCOPE_PARAM, definitionId);
}

/**
 * Scope scan rows to a scheduled definition.
 * Register rows expose definition name (not id); match on normalized name.
 */
export function filterScansByDefinitionScope(
  rows: ScanRegisterRow[],
  definitionName: string | null | undefined,
): ScanRegisterRow[] {
  const target = normalize(definitionName);
  if (!target) return rows;
  return rows.filter((row) => {
    const definition = normalize(row.definition);
    const buyer = normalize(buyerScanDefinitionDisplayName(row.definition));
    return definition === target || buyer === target;
  });
}

function resolveSavedViewTab(raw: string | null | undefined): SavedViewTab | null {
  const value = String(raw || '').trim().toLowerCase();
  return value === 'attention' || value === 'cases' || value === 'scans' || value === 'records'
    ? value
    : null;
}

export function resolveSavedView(raw: string | null | undefined): SavedViewId | null {
  const value = String(raw || '').trim().toLowerCase();
  if ((SAVED_VIEW_IDS as readonly string[]).includes(value)) {
    return value as SavedViewId;
  }
  return null;
}

function resolveAppliedTab(def: WorkspaceSavedViewDef, currentTab?: SavedViewTab | null): SavedViewTab {
  return currentTab && def.supportedTabs.includes(currentTab) ? currentTab : def.tab;
}

function applySavedViewAttention(params: URLSearchParams, def: WorkspaceSavedViewDef): void {
  if (def.attentionFilter) {
    params.set('attention', def.attentionFilter);
  } else {
    params.delete('attention');
  }
}

/** Mutate search params to select or clear a saved view. */
export function applySavedViewToSearchParams(
  params: URLSearchParams,
  viewId: SavedViewId | null,
  currentTab?: SavedViewTab | null,
): void {
  if (!viewId) {
    const previous = resolveSavedView(params.get(SAVED_VIEW_PARAM));
    params.delete(SAVED_VIEW_PARAM);
    if (previous) {
      const prevDef = PRESETS[previous];
      if (prevDef.attentionFilter) {
        params.delete('attention');
      }
    }
    return;
  }
  const def = PRESETS[viewId];
  const targetTab = resolveAppliedTab(def, currentTab ?? resolveSavedViewTab(params.get('tab')));
  params.set(SAVED_VIEW_PARAM, def.id);
  params.set('tab', targetTab);
  applySavedViewAttention(params, def);
}

/** Repair recognized saved-view URLs so the view owns its tab + companion attention. */
export function canonicalizeSavedViewSearchParams(
  params: URLSearchParams,
  currentTab?: SavedViewTab | null,
): boolean {
  const rawView = String(params.get(SAVED_VIEW_PARAM) || '').trim();
  if (!rawView) return false;

  const viewId = resolveSavedView(rawView);
  if (!viewId) {
    params.delete(SAVED_VIEW_PARAM);
    return true;
  }

  const before = params.toString();
  const def = PRESETS[viewId];
  const tab = currentTab ?? resolveSavedViewTab(params.get('tab'));
  // Stay on the current register tab: clear views that don't apply there.
  if (tab && !def.supportedTabs.includes(tab)) {
    params.delete(SAVED_VIEW_PARAM);
    if (def.attentionFilter) {
      params.delete('attention');
    }
    return params.toString() !== before;
  }
  const targetTab = resolveAppliedTab(def, tab);
  params.set(SAVED_VIEW_PARAM, def.id);
  params.set('tab', targetTab);
  applySavedViewAttention(params, def);
  return params.toString() !== before;
}

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function hasNewActivityLabel(value: unknown): boolean {
  const label = normalize(value);
  return Boolean(label) && label !== 'no' && label !== '—' && label !== '-';
}

function isCoverageLabel(value: unknown): boolean {
  return normalize(value) === 'insufficient coverage';
}

/** Cases: open work excludes Closed; coverage/new-activity use row labels. */
export function filterCasesBySavedView(
  rows: CaseRegisterRow[],
  viewId: SavedViewId | null,
  actorIdentity?: string | null,
): CaseRegisterRow[] {
  if (!viewId) return rows;
  switch (viewId) {
    case 'my_open_work':
      {
        const actor = normalize(actorIdentity);
        if (!actor) return [];
        return rows.filter((row) => (
          row.controlStage !== 'Closed' && normalize(row.ownerIdentity) === actor
        ));
      }
    case 'awaiting_signoff':
      return rows.filter((row) => normalize(row.latestScan) === 'awaiting sign-off');
    case 'coverage_issues':
      return rows.filter((row) => isCoverageLabel(row.latestScan));
    case 'new_activity':
      return rows.filter((row) => hasNewActivityLabel(row.newActivity));
    case 'overdue_work':
      return rows.filter((row) => isOverdue(row.dueAt));
    case 'unowned_work':
      return rows.filter((row) => isUnowned(row.owner, row.ownerIdentity));
    default:
      return rows;
  }
}

/**
 * Scans saved views are job filters (workflow / trigger / relevance).
 * No generic Signed view — Records owns the signed archive.
 * Unsupported views for the Scans tab yield [] so a selected view never contradicts rows.
 */
export function filterScansBySavedView(
  rows: ScanRegisterRow[],
  viewId: SavedViewId | null,
): ScanRegisterRow[] {
  if (!viewId) return rows;
  if (!isSavedViewSupported(viewId, 'scans')) return [];
  switch (viewId) {
    case 'awaiting_signoff':
      return rows.filter((row) => row.workflowState === 'Awaiting sign-off');
    case 'failed_blocked':
      return rows.filter((row) => (
        row.workflowState === 'Failed' || row.workflowState === 'Review required'
      ));
    case 'coverage_issues':
      return rows.filter((row) => (
        row.workflowState === 'Insufficient coverage' || isCoverageLabel(row.coverage)
      ));
    case 'scheduled_upcoming':
      return rows.filter((row) => (
        row.workflowState === 'Scheduled' || row.workflowState === 'Queued'
      ));
    case 'latest_per_case':
      return filterToLatestRelevantScans(rows);
    case 'manual_only':
      return rows.filter((row) => normalize(row.trigger) === 'manual');
    default:
      return [];
  }
}

export function filterRecordsBySavedView(
  rows: RecordRegisterRow[],
  viewId: SavedViewId | null,
): RecordRegisterRow[] {
  if (!viewId) return rows;
  switch (viewId) {
    case 'new_activity':
      return rows.filter((row) => hasNewActivityLabel(row.newActivity));
    case 'overdue_work':
    case 'unowned_work':
    case 'my_open_work':
    case 'awaiting_signoff':
    case 'coverage_issues':
    case 'failed_blocked':
    case 'scheduled_upcoming':
    case 'latest_per_case':
    case 'manual_only':
      return [];
    default:
      return rows;
  }
}

function isOverdue(value?: string | null): boolean {
  return isWorkspaceDueDateOverdue(value);
}

function isUnowned(owner?: string | null, ownerIdentity?: string | null): boolean {
  if (String(ownerIdentity || '').trim()) return false;
  const label = normalize(owner).replace(/\s+/g, ' ');
  return !label || label === 'unassigned' || label === 'not assigned' || label === 'not recorded' || label === '—' || label === '-';
}
